import React, { useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Divider from '@material-ui/core/Divider';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';

import { CONSENT_SOURCES } from '../../../constants/whatsapp-contacts';
import { colors } from '../theme';
import ConsentChip from './ConsentChip';
import { KYIV_ZONE } from '../kyivTime';

const useStyles = makeStyles((theme) => ({
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    padding: theme.spacing(0.75, 0),
  },
  label: {
    color: colors.textDim,
    marginRight: theme.spacing(2),
    flexShrink: 0,
  },
  value: {
    fontWeight: 500,
    textAlign: 'right',
    wordBreak: 'break-word',
  },
  legacy: {
    backgroundColor: colors.warnBg,
    color: colors.warnText,
    borderRadius: 6,
    padding: theme.spacing(1, 1.5),
    fontSize: '13px',
    marginTop: theme.spacing(1),
  },
  manual: {
    marginTop: theme.spacing(2),
  },
  hint: {
    color: colors.textDim,
    fontSize: '12px',
    marginBottom: theme.spacing(1.5),
  },
  field: {
    marginBottom: theme.spacing(1.5),
  },
  error: {
    color: '#d32f2f',
    marginTop: theme.spacing(1),
  },
  buttons: {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
  },
  button: {
    textTransform: 'none',
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
}));

const when = (iso) => (iso ? new Date(iso).toLocaleString('uk-UA', { timeZone: KYIV_ZONE }) : '');

const whoWrote = (by) => {
  if (!by) return '';
  if (by === 'client') return 'Клиент (ответ в WhatsApp)';
  if (by === 'system') return 'Система';
  return by;
};

// Информация о подтверждении согласия на рассылку: откуда оно взялось, что ответил
// клиент на приветственную рассылку, и ручная отметка (договор, анкета, звонок).
function ConsentDialog({ contact, onClose, onMark }) {
  const classes = useStyles();
  const [source, setSource] = useState(CONSENT_SOURCES[0]);
  const [evidence, setEvidence] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setSource(CONSENT_SOURCES[0]);
    setEvidence('');
    setError('');
  }, [contact && contact.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!contact) {
    return null;
  }

  const rows = [
    { label: 'Основание', value: contact.consentSource },
    { label: 'Приветствие отправлено', value: when(contact.consentWelcomeAt) },
    { label: 'Ответ клиента', value: contact.consentReply ? `«${contact.consentReply}»` : '' },
    { label: 'Согласие получено', value: when(contact.consentAt) },
    { label: 'Документ или ссылка', value: contact.consentEvidence },
    { label: 'Кто записал', value: whoWrote(contact.consentBy) },
  ].filter((row) => row.value);

  const legacy = contact.consentStatus === 'confirmed' && !contact.consentRecorded;

  const mark = async (status) => {
    setBusy(true);
    setError('');
    let result;
    try {
      // The basis and the document are the reasons for a confirmation. A refusal or a reset
      // must not inherit whatever the form shows ("Договор" by default).
      const confirming = status === 'confirmed';
      result = await onMark(
        contact.id,
        status,
        confirming ? source : '',
        confirming ? evidence.trim() : ''
      );
    } catch (err) {
      result = { ok: false, error: err.message };
    }
    setBusy(false);
    if (result.ok) {
      onClose();
    } else {
      setError(result.error);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Информация о подтверждении</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
          {contact.name}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {contact.company} · {contact.phone}
        </Typography>
        <Divider style={{ margin: '12px 0' }} />

        <div className={classes.row}>
          <span className={classes.label}>Статус</span>
          <ConsentChip status={contact.consentStatus} />
        </div>
        {rows.map((row) => (
          <div key={row.label} className={classes.row}>
            <span className={classes.label}>{row.label}</span>
            <span className={classes.value}>{row.value}</span>
          </div>
        ))}
        {contact.consentStatus === 'none' && (
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            Этому контакту ещё не отправляли запрос. Отправьте приветственную
            рассылку с кнопками «Так / Ні» (раздел «Рассылки»): ответ запишется
            сюда сам.
          </Typography>
        )}
        {contact.consentStatus === 'pending' && (
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            Приветствие отправлено, клиент ещё не нажал кнопку. Пока он не
            ответил, обычные рассылки ему не уходят.
          </Typography>
        )}
        {contact.consentStatus === 'declined' && (
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            Клиент отказался. Писать ему нельзя, сервер такие отправки не
            пропустит. Менеджер отказ не снимает, даже с документом: вернуть
            можно только новым согласием самого клиента: он пишет СТАРТ в
            WhatsApp или нажимает «Так» в запросе согласия.
          </Typography>
        )}
        {legacy && (
          <div className={classes.legacy}>
            Согласие из старой базы: основание записано, документа и ответа
            клиента нет. Лучше отправить приветствие с кнопками или внести
            документ ниже.
          </div>
        )}

        {contact.consentStatus !== 'declined' && (
          <>
            <Divider style={{ margin: '16px 0 0' }} />
            <div className={classes.manual}>
              <Typography variant="subtitle2">Записать вручную</Typography>
              <Typography className={classes.hint}>
                Если согласие получено не в WhatsApp: договор, анкета или звонок.
                Без документа такая запись слабое доказательство.
              </Typography>
              <TextField
                select
                fullWidth
                size="small"
                variant="outlined"
                label="Основание согласия"
                className={classes.field}
                value={source}
                disabled={busy}
                onChange={(event) => setSource(event.target.value)}
              >
                {CONSENT_SOURCES.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Номер договора или ссылка на скан"
                className={classes.field}
                value={evidence}
                disabled={busy}
                inputProps={{ maxLength: 255 }}
                onChange={(event) => setEvidence(event.target.value)}
              />
              <div className={classes.buttons}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  className={classes.button}
                  disabled={busy}
                  onClick={() => mark('confirmed')}
                >
                  Отметить подтверждённым
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  className={classes.button}
                  disabled={busy}
                  onClick={() => mark('declined')}
                >
                  Клиент отказался
                </Button>
                {contact.consentRecorded && (
                  <Button
                    size="small"
                    className={classes.button}
                    disabled={busy}
                    onClick={() => mark('none')}
                  >
                    Сбросить
                  </Button>
                )}
              </div>
              {error && <div className={classes.error}>{error}</div>}
            </div>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Закрыть
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ConsentDialog;
